import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  cn,
  FailureNotice,
  FieldMessage,
  InlineConfirmation,
  LoadingState,
  Notice,
  Select,
  Switch,
  TextInput,
  Tooltip,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { LIMITS } from '@athena/shared'
import { useEffect, useRef, useState, type ReactElement } from 'react'

import {
  PracticeCatalogDocument,
  SubscriptionUsageDocument,
  UpdatePracticeSettingsDocument,
  type PracticeCardFieldsFragment,
} from '../../graphql/generated/operations.js'
import { useUserErrorText } from '../../lib/user-errors.js'

import {
  HALF_HOUR_TIMES,
  timeLabel,
  toAvailabilityInstant,
  toAvailabilityParts,
  type AvailabilityParts,
} from './availability.js'

export interface SettingsPanelProps {
  practice: PracticeCardFieldsFragment
  /** Field composition when the form sits in Practice detail's supporting rail. */
  layout?: 'standard' | 'contextual-rail'
}

/** The controls a validation message can belong to. */
type Field = 'subject' | 'duration' | 'attempts' | 'availableDate' | 'availableTime'

/**
 * Everything a tutor can set about a practice, and one Save.
 *
 * This replaces three panels that each had their own button. The merge is not
 * tidying: three mutations behind one screen is three ways for a save to
 * half-happen, and a tutor who changed the subject and the time limit could
 * end up with one of them. Now the form sends every setting in one request and
 * the server commits them together.
 *
 * The form reads versioned settings from the draft when one is open, and from
 * the published version otherwise. A time limit belongs to a version, so its
 * controls are editable only while a draft exists; saving practice-level
 * settings never creates a draft implicitly.
 *
 * Nothing here is a control. The bounds below are the ones the server
 * enforces, checked here so the tutor is told on the form rather than by a
 * round trip.
 */
export function SettingsPanel({ practice, layout = 'standard' }: SettingsPanelProps) {
  const t = useTranslate()
  const language = useLanguage()
  const userErrorText = useUserErrorText()
  const { data: catalog } = useQuery(PracticeCatalogDocument, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })
  const { data: subscription } = useQuery(SubscriptionUsageDocument)
  /**
   * No `refetchQueries`. The payload carries the whole practice, so Apollo
   * normalises it into the cache and the screen updates from the one response
   * — which is what "one save, one request" has to mean to be worth claiming.
   */
  const [save, { loading }] = useMutation(UpdatePracticeSettingsDocument)

  // The version the form edits, and the one the server will write to.
  const version = practice.draft ?? practice.publishedVersion

  const [subjectId, setSubjectId] = useState(practice.subject.id)
  const [timed, setTimed] = useState(version?.timed ?? false)
  const [duration, setDuration] = useState(
    version?.durationMinutes == null ? '' : String(version.durationMinutes),
  )
  const [attempts, setAttempts] = useState(
    practice.maxAttempts === null ? '' : String(practice.maxAttempts),
  )
  const [availability, setAvailability] = useState<AvailabilityParts>(() =>
    toAvailabilityParts(practice.availableUntil ?? null),
  )
  /**
   * What went wrong, and which control it is about.
   *
   * Field-shaped rather than a banner, so a refused value marks the control it
   * came from — `aria-invalid` and a message tied to the input by
   * `aria-describedby`, which the field atoms do for us. `field: null` is for
   * the failures that belong to the save rather than to a value.
   */
  const [error, setError] = useState<{ field: Field | null; message: string }>()
  const [confirmation, setConfirmation] = useState<string>()
  /** A save that never got an answer: the fields stay as they are, and this offers to send them again. */
  const [failure, setFailure] = useState<unknown>()

  // "Settings saved" is about the values that were saved. The moment one of
  // them changes it is no longer true of what is on screen, so it goes.
  useEffect(() => {
    setConfirmation(undefined)
  }, [subjectId, timed, duration, attempts, availability])

  /**
   * A refused value takes focus once the save has rendered its message, so a
   * keyboard or screen-reader user lands on the control to fix — and hears
   * its message, which the field atoms tie to it — instead of on a button
   * that did nothing visible.
   */
  const root = useRef<HTMLDivElement>(null)
  const focusInvalid = useRef(false)
  useEffect(() => {
    if (!focusInvalid.current) return
    focusInvalid.current = false
    root.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  })
  function refuseField(field: Field, message: string) {
    focusInvalid.current = true
    setError({ field, message })
  }

  /**
   * The error prop for one control: present when the message is about it, and
   * absent otherwise.
   *
   * Spread rather than passed, because `exactOptionalPropertyTypes` makes
   * `error: undefined` a different thing from no `error` at all.
   */
  const errorFor = (field: Field) => (error?.field === field ? { error: error.message } : {})

  const subjects = catalog?.availableSubjects ?? []
  const subjectOptions = withCurrent(
    subjects.map((row) => ({ value: row.id, label: row.name })),
    { value: practice.subject.id, label: practice.subject.name },
  )
  const timeOptions = HALF_HOUR_TIMES.map((time) => ({
    value: time,
    label: timeLabel(time, language),
  }))
  const fieldGrid =
    layout === 'contextual-rail'
      ? 'grid gap-4 md:grid-cols-2 desktop:grid-cols-1'
      : 'grid gap-4 md:grid-cols-2'
  // The server's statement of what the plan in force includes. It enforces
  // each of these itself; this decides only which controls to offer.
  const features = subscription?.mySubscription.features
  const canConfigureAttempts = features?.tutorConfigurableAttempts ?? false
  const canConfigureAvailability = features?.availabilityWindow ?? false
  const canUseTimedPractices = features?.timedPractices ?? false
  /**
   * A paid-only setting the tutor chose while paid and their plan no longer
   * includes. It is preserved, and it still applies — the timer blocks new
   * attempts, the end date still closes the practice — so the form offers the
   * one change Free allows: switching it off. Read from what is saved, not
   * from the form, so the notice stays until the save that lifts it.
   */
  const timedWithoutPlan = !canUseTimedPractices && Boolean(version?.timed)
  const endDateWithoutPlan = !canConfigureAvailability && practice.availableUntil != null

  /**
   * Turning the timer on proposes one attempt.
   *
   * A timed practice is an exam, and an exam is usually sat once — so the form
   * fills that in rather than leaving the tutor to think of it. It happens
   * here, in the switch's own handler, and nowhere else: that is what makes it
   * an initial value rather than a rule. A tutor who then types 2 keeps 2, for
   * as long as the timer stays on and afterwards.
   *
   * Switching the timer off leaves the attempt limit alone. Undoing a number
   * the tutor may have since chosen on purpose would be the form having a
   * second opinion about it.
   */
  function onTimedChange(next: boolean) {
    setTimed(next)
    if (next) setAttempts('1')
  }

  async function onSave() {
    if (
      subjectId !== practice.subject.id &&
      !subjects.some((subject) => subject.id === subjectId)
    ) {
      refuseField('subject', t('userError.SUBJECT_UNAVAILABLE'))
      return
    }
    setError(undefined)
    setConfirmation(undefined)

    const parsedAttempts = attempts.trim() === '' ? null : Number(attempts)
    if (
      parsedAttempts !== null &&
      (!Number.isInteger(parsedAttempts) ||
        parsedAttempts < 1 ||
        parsedAttempts > LIMITS.MAX_ATTEMPTS)
    ) {
      refuseField('attempts', t('practice.fields.maxAttemptsInvalid'))
      return
    }

    const parsedDuration = duration.trim() === '' ? null : Number(duration)
    if (
      timed &&
      (parsedDuration === null ||
        !Number.isInteger(parsedDuration) ||
        parsedDuration < LIMITS.MIN_PRACTICE_DURATION_MINUTES ||
        parsedDuration > LIMITS.MAX_PRACTICE_DURATION_MINUTES)
    ) {
      refuseField(
        'duration',
        t('practice.fields.durationInvalid', [
          LIMITS.MIN_PRACTICE_DURATION_MINUTES,
          LIMITS.MAX_PRACTICE_DURATION_MINUTES,
        ]),
      )
      return
    }

    const availableUntil = toAvailabilityInstant(availability)
    if (!availableUntil.ok) {
      const missingDate = availableUntil.problem === 'MISSING_DATE'
      refuseField(
        missingDate ? 'availableDate' : 'availableTime',
        t(
          missingDate
            ? 'practice.fields.availableUntilNeedsDate'
            : 'practice.fields.availableUntilNeedsTime',
        ),
      )
      return
    }

    // One request, every setting. Nothing below runs a second mutation.
    let result
    try {
      result = await save({
        variables: {
          practiceId: practice.id,
          subjectId,
          availableUntil: availableUntil.value,
          maxAttempts: parsedAttempts,
          timed,
          // Keep the value while the switch is off so turning it back on
          // restores the tutor's previous duration.
          durationMinutes: parsedDuration,
        },
      })
    } catch (caught) {
      // A request that never got an answer leaves every field exactly as the
      // tutor left it: this says so and touches no state. Retrying sends the
      // fields as they are then, which is safe — the save writes the whole
      // set, so sending it twice leaves the same settings.
      setFailure(caught)
      return
    }
    setFailure(undefined)

    const payload = result.data?.updatePracticeSettings
    const userError = payload?.userErrors[0]
    if (!payload || userError) {
      if (!userError) {
        setError({ field: null, message: t('practice.fields.settingsSaveFailed') })
        return
      }
      // The server's refusals are about the practice rather than about one
      // control — a subject that left the catalog, a practice with no version
      // to put a limit on — so they read as the section's error.
      setError({ field: null, message: userErrorText(userError) })
      return
    }
    setConfirmation(t('practice.fields.settingsSaved'))
  }

  if (!features) return <LoadingState>{t('common.loading')}</LoadingState>

  return (
    <div ref={root} className="flex flex-col gap-4">
      {/*
       * What the practice is, and how many goes at it — the two settings that
       * are always here, so they are the row that is always full.
       *
       * Each used to have a `grid md:grid-cols-2` of its own holding one
       * field, which meant Subject sat in a half-width cell beside nothing,
       * and Max attempts did too whenever the timer was off. Two half-empty
       * rows, in the panel's most common state. One grid over the pair fills
       * both cells instead, and the fields keep exactly the width they had.
       */}
      <div className={fieldGrid}>
        <Select
          label={t('practice.fields.subject')}
          helperText={t('practice.fields.subjectHelper')}
          options={subjectOptions}
          value={subjectId}
          {...errorFor('subject')}
          onValueChange={setSubjectId}
        />

        <PremiumFeature locked={!canConfigureAttempts} message={t('practice.fields.premiumOnly')}>
          <TextInput
            type="number"
            min={1}
            max={LIMITS.MAX_ATTEMPTS}
            className="max-w-32"
            label={t('practice.fields.maxAttempts')}
            helperText={t('practice.fields.maxAttemptsHelper')}
            value={attempts}
            disabled={!canConfigureAttempts}
            {...errorFor('attempts')}
            onChange={(event) => setAttempts(event.target.value)}
          />
        </PremiumFeature>
      </div>

      {/* The timer, as one group: the switch, what it does, its duration,
          and what changing it costs. The last of those used to sit under
          availability, two settings away from the switch it explains. */}
      <div className="flex flex-col gap-4 border-t border-border pt-4">
        {/* Locked only while it is off: a time limit a Free plan no longer
            includes can still be turned off, never on. */}
        <PremiumFeature
          locked={!canUseTimedPractices && !timed}
          message={t('practice.fields.premiumOnly')}
        >
          <Switch
            checked={timed}
            onCheckedChange={onTimedChange}
            label={t('practice.fields.timed')}
            disabled={(!canUseTimedPractices && !timed) || !practice.draft}
          />
        </PremiumFeature>
        <FieldMessage>{t('practice.fields.timedHelper')}</FieldMessage>
        {timedWithoutPlan ? (
          <Notice tone="warning" title={t('practice.fields.timedWithoutPlan')} />
        ) : null}

        {/* The timer's own field, directly under the switch that reveals it, and
          one column wide like every other field rather than stretched across
          the panel. It is the one cell that stands alone, and it reads as
          belonging to the control above it rather than as a gap in a row.

          Absent rather than disabled when the timer is off: a greyed-out field
          is a question the tutor has to answer, and the switch already says
          why it is not there. */}
        {timed ? (
          <div className={fieldGrid}>
            <TextInput
              type="number"
              min={LIMITS.MIN_PRACTICE_DURATION_MINUTES}
              max={LIMITS.MAX_PRACTICE_DURATION_MINUTES}
              className="max-w-32"
              label={t('practice.fields.duration')}
              helperText={t('practice.fields.durationHelper', [
                LIMITS.MIN_PRACTICE_DURATION_MINUTES,
                LIMITS.MAX_PRACTICE_DURATION_MINUTES,
              ])}
              value={duration}
              disabled={!practice.draft}
              {...errorFor('duration')}
              onChange={(event) => setDuration(event.target.value)}
            />
          </div>
        ) : null}

        <FieldMessage>{t('practice.fields.timerVersionNote')}</FieldMessage>
      </div>

      {/* Date and time as two controls, because the time is a list of half
          hours rather than a free minute. Same row, so they read as one
          setting — and a group of its own, below a divider. */}
      <PremiumFeature locked={!canConfigureAvailability} message={t('practice.fields.premiumOnly')}>
        <div className={cn(fieldGrid, 'border-t border-border pt-4')}>
          <TextInput
            type="date"
            label={t('practice.fields.availableUntil')}
            helperText={t('practice.fields.availableUntilHelper')}
            value={availability.date}
            disabled={!canConfigureAvailability}
            {...errorFor('availableDate')}
            onChange={(event) =>
              setAvailability((parts) => ({ ...parts, date: event.target.value }))
            }
          />
          <Select
            label={t('practice.fields.availableUntilTime')}
            helperText={t('practice.fields.availableUntilTimeHelper')}
            placeholder={t('practice.fields.availableUntilTimePlaceholder')}
            options={timeOptions}
            disabled={!canConfigureAvailability}
            {...errorFor('availableTime')}
            value={availability.time}
            onValueChange={(time) => setAvailability((parts) => ({ ...parts, time }))}
          />
        </div>
      </PremiumFeature>

      {/* The end date set while paid still closes the practice. Removing it is
          the change a Free plan allows, and it takes effect with the save below
          like every other setting here. */}
      {endDateWithoutPlan ? (
        <Notice
          tone="warning"
          title={t('practice.fields.endDateWithoutPlan')}
          actions={
            availability.date !== '' || availability.time !== '' ? (
              <Button
                variant="secondary"
                onClick={() => setAvailability(toAvailabilityParts(null))}
              >
                {t('practice.fields.removeEndDate')}
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {/* Only the failures that belong to the save rather than to a value; the
          rest are on their own controls, where the tutor is looking. */}
      {error?.field === null ? <FieldMessage tone="error">{error.message}</FieldMessage> : null}
      {failure === undefined ? null : (
        <FailureNotice
          title={t('practice.fields.settingsNotSaved')}
          error={failure}
          retrying={loading}
          onRetry={() => void onSave()}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        {/* The design system's default size, which is the same `control-md`
            height every field above it uses. No size prop anywhere in this
            section: that is what keeps the heights equal. */}
        <Button onClick={() => void onSave()} loading={loading} loadingText={t('common.saving')}>
          {t('practice.fields.saveSettings')}
        </Button>
        {confirmation ? <InlineConfirmation>{confirmation}</InlineConfirmation> : null}
        {/* A standing fact about the practice, not the result of this save, so
            it is plain helper text: nothing to announce each time the panel
            is drawn. */}
        {practice.draft ? (
          <FieldMessage>{t('practice.unpublishedChangesNote')}</FieldMessage>
        ) : null}
      </div>
    </div>
  )
}

function PremiumFeature({
  locked,
  message,
  children,
}: {
  locked: boolean
  message: string
  children: ReactElement
}) {
  if (!locked) return children

  return (
    <Tooltip content={message} long>
      <div tabIndex={0} aria-label={message} className="cursor-not-allowed rounded-sm">
        {children}
      </div>
    </Tooltip>
  )
}

interface Option {
  value: string
  label: string
}

/** The catalog's options, plus the one already chosen if it has left the catalog. */
function withCurrent(options: Option[], current: Option): Option[] {
  return options.some((option) => option.value === current.value) ? options : [...options, current]
}
